import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Typography, TextField, Link, Box, Button, CircularProgress } from '@mui/material'
import Grid from '@mui/material/Grid'
import jwt from 'jwt-decode'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import logo from 'assets/logoWhiteTheme.png'
import { sidebarMenus } from 'components/Layout/sidebarMenus'
import { useThemeService } from 'components/Theme/hooks'
import { useLoginService } from 'features/account/hooks'
import { loginActions } from 'features/account/store/login.slice'
import { LoginUserInfo, LoginResponse, RoleObject, Profile } from 'features/account/types'
import { getFallbackLandingPath, parseMenuIds } from 'features/account/utils/landingFallback'
import { useDiscussionService } from 'features/discussions/hooks'
import { useOrganizationService } from 'features/organization/hooks'
import { useReferralsService } from 'features/referrals/hooks'
import { persistor } from 'store/store'
import { setInterceptors } from 'utils/apiService/httpService'
import { setCookie } from 'utils/cookieUtils'
import { getFingerprint } from 'utils/fingerprint'
import { userLevels } from 'utils/genericData/getUserLevels'
import { ChildReferralListPath, ParentReferralListPath } from 'utils/genericData/pathList'
import {
  showApiFailedSnackbar,
  showExceptionSnackbar,
  showSnackbar,
} from 'utils/getSnackbarMessage'
import { initializePendo, isPendoEnabled } from 'utils/pendoFunctions'
import { logTransaction } from 'utils/sentryServices/logApiInfo'

import LeftContainer from '../LeftContainer'
import useStyles from '../useStyles'

type MfaDetails = {
  isMfaRequiredForUser: boolean
  mfaPreferance: number | null
  securityFingerPrint: string | null
  email: string | null
  phoneNumber: string | null
  message: string | null
  emailVerified: boolean
  phoneNumberVerified: boolean
}
type ChatSessionObject = {
  discussionSessionToken: string
  discussionUserId: string
  expiresAt: number
}

const CODE_LENGTH = 6
const RESEND_TIMEOUT = 30 // seconds

export const VerificationCode = () => {
  const {
    login,
    setPermissionIds,
    setMenuIds,
    setSelectedRoleLevel,
    setSelectedFacilities,
    generateLoginMfaCode,
    getProfileDetail,
    setProfileData,
  } = useLoginService()
  const { putActiveTab } = useOrganizationService()
  const { getChatSessionToken } = useDiscussionService()
  const { setReferralListRoot } = useReferralsService()
  const { setTheme } = useThemeService()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [fingerprint, setFingerprint] = useState<string>('')
  type LocationState = {
    mfaDetails?: MfaDetails
    methodType?: number
  }
  const location = useLocation() as { state?: LocationState }
  const [methodType, setMethodType] = useState<number>(1)
  const [mfaDetails, setMfaDetails] = useState<MfaDetails | null>(null)
  useEffect(() => {
    if (location.state) {
      setMethodType(location.state?.methodType ?? 1)
      setMfaDetails(location.state?.mfaDetails ?? null)
    }
  }, [location.state])

  const classes = useStyles()
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [loading, setLoading] = useState(false)
  const [failedAttemptNumber, setFailedAttemptNumber] = useState(0)
  const getLockHeading = () =>
    failedAttemptNumber === 5
      ? t(`login.loginLockedHeading`)
      : `${t(`login.loginFailedHeading`)} ${failedAttemptNumber} ${t(`login.loginFailedHeading2`)}`

  const showLockError = () => {
    if (failedAttemptNumber && failedAttemptNumber !== undefined) {
      const attempt = Number(failedAttemptNumber)
      if (!Number.isNaN(attempt) && attempt > 0) return true
      return false
    }
    return false
  }
  const getLockMessage = () =>
    failedAttemptNumber === 5 ? t(`login.loginLockedText`) : t(`login.loginFailedText`)
  const inputRefs = useRef<HTMLInputElement[]>([])
  const storePermissionAndMenus = (userDetails: LoginUserInfo) => {
    const userRoleLevel = userLevels.find(
      obj => obj?.id.toString() === userDetails?.userlevel?.toString(),
    )?.code
    switch (userRoleLevel) {
      case 'super-admin':
        setPermissionIds(
          userDetails?.superadminlevelpermissions
            ? JSON.parse(userDetails?.superadminlevelpermissions)
            : [],
        )
        setMenuIds(userDetails?.superadminmenu ? JSON.parse(userDetails?.superadminmenu) : [])
        setSelectedRoleLevel(0)
        setSelectedFacilities([])
        break
      case 'organization':
      case 'organization-facility':
        setPermissionIds(
          userDetails?.orglevelpermissions ? JSON.parse(userDetails?.orglevelpermissions) : [],
        )
        setMenuIds(userDetails?.orglevelmenu ? JSON.parse(userDetails?.orglevelmenu) : [])
        setSelectedRoleLevel(1)
        setSelectedFacilities([])
        break
      case 'facility':
        setPermissionIds(
          userDetails?.faclevelpermissions ? JSON.parse(userDetails?.faclevelpermissions) : [],
        )
        setMenuIds(userDetails?.faclevelmenu ? JSON.parse(userDetails?.faclevelmenu) : [])
        setSelectedRoleLevel(2)
        setSelectedFacilities([])
        break
      default:
        setPermissionIds([])
        setMenuIds([])
        setSelectedRoleLevel(0)
        setSelectedFacilities([])
        break
    }
  }
  const getRole = (rolesString?: string) => {
    if (rolesString) {
      const roles: RoleObject[] = JSON.parse(rolesString)
      const currentRole = roles[0]?.Name
      return currentRole ?? ''
    }
    return ''
  }

  const pendoInit = (userData: LoginUserInfo) => {
    if (userData.userId && isPendoEnabled()) {
      const pendoInitData = {
        id: userData.userId,
        email: userData.email ?? '',
        role: getRole(userData.roles),
        orgId: userData.organizationId ? String(userData.organizationId) : null,
        orgName: userData.organizationName,
      }
      initializePendo(pendoInitData)
    }
  }

  const flattenedSidebarMenus = sidebarMenus.flatMap(menuItem => {
    if (menuItem.items) {
      return menuItem.items
    }
    return menuItem
  })
  const getUserLevel = (userDt: LoginUserInfo) =>
    userLevels.find(obj => obj?.id.toString() === userDt?.userlevel?.toString())?.code

  const seedFacilitiesFromProfile = async (userId: string) => {
    const res = await getProfileDetail(userId)
    if (!res?.data?.isSuccess) throw new Error('profile_fetch_failed')
    const profile = res.data.data as Profile
    setProfileData(profile)
    setSelectedFacilities(profile?.facilityIds ?? [])
  }
  const defLandingPage = (levelName: string, menuObjects: string) => {
    const menuArr = parseMenuIds(menuObjects)
    switch (levelName) {
      case 'super-admin':
        navigate('/dashboard/view')
        break
      case 'organization':
      case 'organization-facility':
        if (menuArr.includes(16)) {
          navigate(ParentReferralListPath)
        } else navigate(getFallbackLandingPath(menuArr))
        break
      case 'facility':
        if (menuArr.includes(17)) {
          navigate(ChildReferralListPath)
        } else navigate(getFallbackLandingPath(menuArr))
        break
      default:
        navigate(getFallbackLandingPath(menuArr))
        break
    }
  }

  const returnUserMenuIds = (roleName: string, useDet: LoginUserInfo) => {
    switch (roleName) {
      case 'super-admin':
        return useDet?.superadminmenu
      case 'organization':
      case 'organization-facility':
        return useDet?.orglevelmenu
      case 'facility':
        return useDet?.faclevelmenu
      default:
        return useDet?.faclevelmenu
    }
  }

  const getReferralListView = (refView?: string) => {
    if (refView === '1') {
      return 'table'
    }
    return 'grid'
  }
  const goToLandingPage = (userData: LoginUserInfo) => {
    const userLevel = getUserLevel(userData) ?? ''
    const menuToCheck = returnUserMenuIds(userLevel, userData) ?? ''
    const menuIds = parseMenuIds(menuToCheck)

    if (menuIds.length === 0) {
      // The token was issued with no menus for this level (#18775). Record the
      // identifiers backend needs to trace the provisioning; the user lands on /no-access.
      logTransaction('Login: token carries no menus for user level', {
        userId: userData.userId,
        userlevel: userData.userlevel,
        organizationId: userData.organizationId,
      })
    }

    if (
      !userData.landingpageId ||
      userData.landingpageId === '0' ||
      userData.landingpageId === ''
    ) {
      defLandingPage(userLevel, menuToCheck)
      return
    }

    // Membership must be checked on the parsed list — a substring test on the JSON
    // string matched "1" inside "[12,17]" and routed users into modules they lack.
    if (!menuIds.includes(Number(userData.landingpageId))) {
      defLandingPage(userLevel, menuToCheck)
      return
    }

    const menuObject = flattenedSidebarMenus.find(
      it => it?.backendId?.toString() === userData.landingpageId,
    )

    if (!menuObject || !menuObject.active) {
      navigate(getFallbackLandingPath(menuIds))
      return
    }
    if (menuObject.backendId === 13) {
      navigate(`/organization/view?id=${userData.organizationId}`)
    } else {
      navigate(menuObject.path)
    }
  }
  const dispatch = useDispatch()

  useEffect(() => {
    const fetchFingerprint = async () => {
      const fp = await getFingerprint()
      setFingerprint(fp)
    }
    fetchFingerprint()
  }, [])

  useEffect(() => {
    setTheme('light')
    putActiveTab(0)
    persistor.purge()
  }, [])

  const handleChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code]
      newCode[index] = value
      setCode(newCode)
      if (value && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleContinue = () => {
    setLoading(true)
    const values = {
      grant_type: 'password',
      scope: 'openid offline_access referralscope',
      client_id: 'gccweb',
      client_secret: '?M[Qc;8&T^&UHyu',
      mfa_code: code.join(''),
      mfa_type: methodType,
      device_fingerprint: fingerprint,
      device_type: 3,
      security_fingerprint: mfaDetails?.securityFingerPrint ?? undefined,
    }
    login(values)
      .then((res: LoginResponse) => {
        if (res.data.isSuccess) {
          const user: LoginUserInfo = jwt(res?.data?.data?.accessToken)
          storePermissionAndMenus(user)
          dispatch(loginActions.loginSucceeded(user))

          if (user?.loginCount && user?.loginCount > 0) {
            setInterceptors('application/json', res?.data?.data?.accessToken)
            getChatSessionToken().then(response => {
              if (response.data.isSuccess) {
                const chatSessionData = response?.data?.data as ChatSessionObject
                setCookie('gcc_chat_token', chatSessionData?.discussionSessionToken)
                setCookie('chat_user_id', chatSessionData?.discussionUserId)
              } else {
                showApiFailedSnackbar(response?.data?.message)
              }
            })

            setCookie('gcc_access_token', res?.data?.data?.accessToken)
            setCookie('gcc_refresh_token', res?.data?.data?.refreshToken)
            // Always stamp the login type — an SSO user can also land here, and
            // leaving a previous session's 'sso' value would send this session
            // down the IdP-redirect branch on a 401 instead of using the refresh
            // token that the MFA grant just issued.
            setCookie('login_type', 'normal')

            pendoInit(user)

            const proceedToLanding = () => {
              goToLandingPage(user)

              setLoading(false)

              const refViewType = getReferralListView(user?.referralviewType)

              setReferralListRoot(refViewType)
            }

            if (getUserLevel(user) === 'facility') {
              seedFacilitiesFromProfile(user?.userId ?? '')
                .then(proceedToLanding)
                .catch(() => {
                  setLoading(false)
                  showSnackbar(false, 'Unable to load your facility access. Please try again.')
                })
            } else {
              proceedToLanding()
            }
          } else {
            navigate('/resetpassword')
          }
        } else {
          if (
            res?.data?.message === 'login_failed' ||
            res?.data?.data === 'Invalid security fingerprint'
          ) {
            showSnackbar(false, 'Please log in again as the session has expired.')
            navigate('/')

            setFailedAttemptNumber(res?.data?.data as number)
          } else if (res?.data?.message === 'login_locked') {
            setFailedAttemptNumber(5)
          } else if (res?.data?.message === 'otp_invalid') {
            showSnackbar(false, t(`notifications.${res.data.message}`))
          } else {
            setFailedAttemptNumber(0)
            showSnackbar(false, t(`notifications.${res.data.message}`))
          }
          setLoading(false)
        }
      })
      .catch((error: unknown) => {
        setLoading(false)
        showExceptionSnackbar(error)
      })
  }

  const [timer, setTimer] = useState(RESEND_TIMEOUT)
  const [canResend, setCanResend] = useState(false)

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1)
      }, 1000)
    } else {
      setCanResend(true)
    }
    return () => clearInterval(interval)
  }, [timer])

  const callGenerateLoginMfaCode = () => {
    const otpData = {
      user_email: mfaDetails?.email ?? '',
      mfa_type: typeof methodType === 'number' ? methodType : 1,
      mfac_code_generationreason: 1,
      phone_number: mfaDetails?.phoneNumber ?? '',
      security_fingerprint: mfaDetails?.securityFingerPrint ?? '',
      client_id: 'gccweb',
      client_secret: '?M[Qc;8&T^&UHyu',
    }
    return generateLoginMfaCode(otpData)
  }

  const handleResend = () => {
    if (!canResend) return
    callGenerateLoginMfaCode().then(response => {
      if (response.data.isSuccess) {
        setCode(Array(CODE_LENGTH).fill(''))
        inputRefs.current[0]?.focus()
        setCanResend(false)
        setTimer(RESEND_TIMEOUT)
      } else {
        if (response?.data?.message === 'invalid_security_fingerprint') {
          showSnackbar(false, 'Please log in again as the session has expired.')
          navigate('/')
        }
        if (response?.data?.message === 'unauthorized') {
          showSnackbar(false, 'Unauthorized request. Please try again.')
          navigate('/')
        }
      }
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      if (!code.includes('') && !loading) {
        handleContinue()
      }
    }
  }

  if (!mfaDetails) {
    navigate('/')
    return null
  }

  return (
    <Grid container height={'100vh'}>
      <LeftContainer />
      <Grid item md={12} lg={8} className={classes.rightColumn}>
        <Box maxWidth={400} mx="auto" mt={8} px={2}>
          <Box mb={3}>
            <img src={logo} alt="ReferralIQ" style={{ width: 130 }} />
          </Box>
          {showLockError() ? (
            <Grid item width={'100%'} mt={1}>
              <Grid container flexDirection={'column'} className={classes.loginErrorContainer}>
                <Grid item>
                  <ErrorOutlineIcon className={classes.loginErrorIcon} />
                </Grid>
                <Grid item>
                  <Typography className={classes.loginErrorHeading}>{getLockHeading()}</Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.loginErrorText}>{getLockMessage()}</Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : null}

          <Typography variant="h6" fontWeight={600} mb={1}>
            Verification Code
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Please type in the code we just sent to your{' '}
            {methodType === 1 ? 'email' : 'phone number'}
          </Typography>

          <Box display="flex" gap={1.5} mb={2}>
            {code.map((digit, i) => (
              <TextField
                sx={{ color: '#5f5f5f' }}
                key={i}
                inputRef={ref => {
                  if (ref) inputRefs.current[i] = ref
                }}
                value={digit}
                onChange={e => {
                  const val = e.target.value

                  if (val.length === CODE_LENGTH && i === 0 && /^\d{6}$/.test(val)) {
                    const newCode = val.split('')
                    setCode(newCode)
                    setTimeout(() => inputRefs.current[CODE_LENGTH - 1]?.focus(), 0)
                    return
                  }

                  handleChange(i, val)
                }}
                onPaste={e => {
                  const pasted = e.clipboardData.getData('text')
                  if (/^\d{6}$/.test(pasted)) {
                    e.preventDefault()
                    const newCode = pasted.split('')
                    setCode(newCode)
                    setTimeout(() => inputRefs.current[CODE_LENGTH - 1]?.focus(), 0)
                  }
                }}
                onKeyDown={e => handleKeyDown(e, i)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    width: '45px',
                    height: '45px',
                    textAlign: 'center',
                    fontSize: '20px',
                    padding: 0,
                    color: '#5f5f5f',
                  },
                }}
                variant="outlined"
              />
            ))}
          </Box>

          <Typography variant="caption" mb={3}>
            Didn’t get a code?{' '}
            <Link
              href="#"
              underline="always"
              fontWeight={500}
              onClick={() => {
                handleResend()
                setTimer(RESEND_TIMEOUT)
              }}
              color={canResend ? 'primary' : 'text.disabled'}
              sx={{ pointerEvents: canResend ? 'auto' : 'none' }}
            >
              {canResend ? 'Resend code' : `Resend in ${timer}s`}
            </Link>
          </Typography>

          <Grid container spacing={1} mt={2}>
            <Grid item xs={5}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => {
                  if (mfaDetails?.mfaPreferance) {
                    navigate('/')
                  } else {
                    navigate('/login')
                  }
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={5}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={code.includes('') || loading ? undefined : handleContinue}
                sx={{
                  opacity: code.includes('') ? 0.5 : 1,
                  cursor: code.includes('') || loading ? 'default' : 'pointer',
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Continue'}
              </Button>
              <form
                onSubmit={e => {
                  e.preventDefault()
                  if (!code.includes('') && !loading) handleContinue()
                }}
                style={{ display: 'none' }}
              >
                <input type="submit" />
              </form>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  )
}
